// ---------------------------------------------------------------------------
// The Sales Rep step's rules (R17b)
//
// Who is assigned at what share, which account a rep just ticked gets, which
// channel a rep still owes an account for, what the edit of a campaign past
// 'draft' may apply, and how the two mailbox lists become one. Taken out of
// wizard/wiz-campaign-type.ts and pages/campaign-wizard.ts unchanged, so the
// step's rebuild (R17d) redoes only the markup. Plain TypeScript.
// ---------------------------------------------------------------------------

import { type AccountBuckets, hasRepSetChange, senderAccountApplyAction } from '../sender-account-plan.js';
import type { CampaignDraft, ChannelFallback, SalesRep, SalesRepAssignment } from '../types.js';
import { channelsRequiringAccount } from '../types.js';
import type { AccountSeed } from './draft.js';
import { SALES_REP_ACCOUNT_BLOCK } from './steps.js';

/** The channels a rep can owe an account for. */
export type RepChannel = 'email' | 'linkedin';

/** Which channels the sequence sends on (only those need an account). */
export type RequiredChannels = { email: boolean; linkedin: boolean };

/** Distribute 100% as evenly as possible across N integer buckets (the first ones get the remainder). */
export const evenSplit = (n: number): number[] => {
  if (n <= 0) return [];
  const base = Math.floor(100 / n);
  const remainder = 100 - base * n;
  return Array.from({ length: n }, (_, i) => base + (i < remainder ? 1 : 0));
};

/** The same reps, split evenly. */
export const equalShares = (assignments: SalesRepAssignment[]): SalesRepAssignment[] => {
  const split = evenSplit(assignments.length);
  return assignments.map((a, i) => ({ ...a, percentage: split[i] ?? 0 }));
};

/** A rep's own accounts, as the account lists name them (an address for a mailbox, an id for LinkedIn). */
export interface OwnAccounts {
  email: string[];
  linkedin: string[];
}

/**
 * A rep ticked or unticked. Unticked: the rest split evenly. Ticked: added at
 * the end, all split evenly, and for every channel the campaign sends on where
 * the rep has exactly one account and none picked yet, that account comes in
 * the same change (`seed`) — there is no decision to make there, and leaving it
 * empty is what turns "switch the Sales Rep" into a rejected save. With several
 * accounts nothing is guessed; the hints ask for a pick instead.
 */
export function toggledRep(args: {
  assignments: SalesRepAssignment[];
  id: string;
  required: RequiredChannels;
  emailAccountsByAie: AccountBuckets;
  linkedinAccountIdsByAie: AccountBuckets;
  own: OwnAccounts;
}): { assignments: SalesRepAssignment[]; seed?: AccountSeed } {
  const { assignments, id, required, emailAccountsByAie, linkedinAccountIdsByAie, own } = args;
  if (assignments.some(a => a.aiEmployeeId === id)) {
    const remaining = assignments.filter(a => a.aiEmployeeId !== id);
    const split = evenSplit(remaining.length);
    return { assignments: remaining.map((a, i) => ({ aiEmployeeId: a.aiEmployeeId, percentage: split[i] ?? 0 })) };
  }
  const next = [...assignments, { aiEmployeeId: id, percentage: 0 }];
  const split = evenSplit(next.length);
  const seed: AccountSeed = {};
  if (required.email && (emailAccountsByAie[id] ?? []).length === 0 && own.email.length === 1)
    seed.emailAccountsByAie = { [id]: [own.email[0]!] };
  if (required.linkedin && (linkedinAccountIdsByAie[id] ?? []).length === 0 && own.linkedin.length === 1)
    seed.linkedinAccountIdsByAie = { [id]: [own.linkedin[0]!] };
  return { assignments: next.map((a, i) => ({ ...a, percentage: split[i] ?? 0 })), seed };
}

/**
 * One rep's share set by hand. With a single rep the share is always 100%.
 * Otherwise the value is held to 0..100 and the remaining share goes to the
 * OTHER reps so the total is always exactly 100%: in proportion to what they
 * had (the rounding error on the last of them), evenly when they all had 0.
 * Null: nothing to change (no rep).
 */
export function withShare(assignments: SalesRepAssignment[], id: string, value: number): SalesRepAssignment[] | null {
  if (assignments.length <= 1) {
    return assignments.length === 1 ? assignments.map(a => ({ ...a, percentage: 100 })) : null;
  }
  const v = Math.max(0, Math.min(100, Math.round(value)));
  const others = assignments.filter(a => a.aiEmployeeId !== id);
  const remainder = 100 - v;
  const prevOthersTotal = others.reduce((s, a) => s + (a.percentage || 0), 0);
  let othersPct: number[];
  if (prevOthersTotal > 0) {
    // Proportional split, then fix rounding error on the last bucket.
    const raw = others.map(a => (a.percentage / prevOthersTotal) * remainder);
    othersPct = raw.map(n => Math.round(n));
    const diff = remainder - othersPct.reduce((s, n) => s + n, 0);
    if (othersPct.length > 0) othersPct[othersPct.length - 1]! += diff;
  } else {
    // Even split (handles the case when others were all 0).
    const base = Math.floor(remainder / others.length);
    const extra = remainder - base * others.length;
    othersPct = others.map((_, i) => base + (i < extra ? 1 : 0));
  }
  let oi = 0;
  return assignments.map(a => {
    if (a.aiEmployeeId === id) return { ...a, percentage: v };
    const next = { ...a, percentage: Math.max(0, Math.min(100, othersPct[oi] ?? 0)) };
    oi++;
    return next;
  });
}

/** One account of a rep ticked or unticked. */
export const toggledAccount = (buckets: AccountBuckets, aieId: string, account: string): AccountBuckets => {
  const current = buckets[aieId] ?? [];
  const next = current.includes(account) ? current.filter(e => e !== account) : [...current, account];
  return { ...buckets, [aieId]: next };
};

/**
 * Channels this Sales Rep still owes a sending account for — empty when
 * they're good to go. Only channels the sequence actually sends on count, and
 * a rep who borrows someone else's account through a channel fallback is
 * covered too. The server applies exactly this rule, so anything listed here
 * is a save that would come back rejected.
 */
export function missingChannelsForRep(
  aieId: string,
  args: {
    required: RequiredChannels;
    emailAccountsByAie: AccountBuckets;
    linkedinAccountIdsByAie: AccountBuckets;
    channelFallbacks: ChannelFallback[];
  },
): RepChannel[] {
  const { required, emailAccountsByAie, linkedinAccountIdsByAie, channelFallbacks } = args;
  const covered = (channel: RepChannel) =>
    channelFallbacks.some(f => f.sourceAiEmployeeId === aieId && f.channel === channel);
  const missing: RepChannel[] = [];
  if (required.email && (emailAccountsByAie[aieId] ?? []).length === 0 && !covered('email')) missing.push('email');
  if (required.linkedin && (linkedinAccountIdsByAie[aieId] ?? []).length === 0 && !covered('linkedin'))
    missing.push('linkedin');
  return missing;
}

/** The quiet hint's words for what is missing: "an email account and a LinkedIn account". */
export const missingAccountsPhrase = (missing: RepChannel[]): string =>
  missing.map(c => (c === 'email' ? 'an email account' : 'a LinkedIn account')).join(' and ');

/**
 * The rep to assign when nothing is: the default one, else the first listed.
 * Only for a genuinely empty selection, and only once the real assignment is
 * known (`ready`: at once for a new draft, after the campaign loaded for an
 * edit) — acting earlier raced the load and replaced a launched campaign's
 * real Sales Rep with an arbitrary default. `done` once it decided, either way.
 */
export function defaultRepToAssign(args: {
  done: boolean;
  ready: boolean;
  assignments: SalesRepAssignment[];
  reps: SalesRep[];
  repsLoaded: boolean;
}): { decided: boolean; assign?: SalesRepAssignment[] } {
  const { done, ready, assignments, reps, repsLoaded } = args;
  if (done || !ready) return { decided: false };
  if (assignments.length > 0) return { decided: true };
  if (!repsLoaded || reps.length === 0) return { decided: false };
  const def = reps.find(e => e.isDefault) ?? reps[0];
  return { decided: true, assign: def ? [{ aiEmployeeId: def._id, percentage: 100 }] : undefined };
}

/** A mailbox a rep may send from, as the step lists it. */
export interface MailboxOption {
  email: string;
  name: string;
  aiEmployeeId: string | null;
}

/**
 * Two sources, one list — the same merge Settings does. Instantly is the
 * retiring path; a Unipile mailbox is the new one and must show even when
 * Instantly is unconfigured or its plan lapsed (which makes /api/accounts/email
 * answer non-ok), or an email Sales Rep looks unowned. A Unipile mailbox is
 * keyed by the provider's account id, not an address — the same id the launch
 * reads as the Sending Account. The error only when both failed.
 */
// biome-ignore lint/suspicious/noExplicitAny: the two answers as the server sends them
export function mailboxOptions(instantly: any, unipile: any): { accounts: MailboxOption[]; error: string } {
  const fromInstantly: MailboxOption[] = instantly.ok ? (instantly.accounts ?? []) : [];
  const fromUnipile: MailboxOption[] = unipile.ok
    ? // biome-ignore lint/suspicious/noExplicitAny: a Unipile account as the server sends it
      (unipile.accounts ?? []).map((a: any) => ({
        email: a.id,
        name: a.email ?? a.displayName ?? a.id,
        aiEmployeeId: a.aiEmployeeId ?? null,
      }))
    : [];
  return {
    accounts: [...fromInstantly, ...fromUnipile],
    error: !instantly.ok && !unipile.ok ? 'Failed to load accounts' : '',
  };
}

/**
 * Which required channels are missing a sending account on at least one
 * assigned Sales Rep. This is exactly what the /sales-reps endpoint rejects,
 * evaluated up-front so the Sales Rep step can point at the empty account
 * picker instead of letting the user hit Apply and get a red error back. A rep
 * with a configured channel fallback borrows someone else's accounts
 * server-side, so an empty bucket is fine for them.
 */
export function salesRepAccountGaps(d: CampaignDraft): RequiredChannels {
  const need = channelsRequiringAccount(d.sequence);
  const hasFallback = (aiEmployeeId: string, channel: RepChannel) =>
    (d.channelFallbacks ?? []).some(f => f.sourceAiEmployeeId === aiEmployeeId && f.channel === channel);
  return {
    email:
      need.email &&
      d.aiEmployeeAssignments.some(
        a => (d.emailAccountsByAie[a.aiEmployeeId] ?? []).length === 0 && !hasFallback(a.aiEmployeeId, 'email'),
      ),
    linkedin:
      need.linkedin &&
      d.aiEmployeeAssignments.some(
        a => (d.linkedinAccountIdsByAie[a.aiEmployeeId] ?? []).length === 0 && !hasFallback(a.aiEmployeeId, 'linkedin'),
      ),
  };
}

/**
 * What the Apply gate is still waiting for, in two registers: `hint` is the
 * quiet line, shown as soon as something is missing; `error` is what the user
 * gets if they apply anyway. Same condition, different volume. Null once
 * there's nothing left to wait for.
 */
export function salesRepApplyGate(d: CampaignDraft): { hint: string; error: string } | null {
  const both = (message: string) => ({ hint: message, error: message });
  const assignments = d.aiEmployeeAssignments;
  if (assignments.length === 0) return both('Select at least one Sales Representative.');
  if (assignments.reduce((s, a) => s + (a.percentage || 0), 0) !== 100)
    return both('Sales Representative shares must total 100%.');
  const gaps = salesRepAccountGaps(d);
  if (!gaps.email && !gaps.linkedin) return null;
  const what =
    gaps.email && gaps.linkedin
      ? 'an email and a LinkedIn account'
      : gaps.email
        ? 'an email account'
        : 'a LinkedIn account';
  return { hint: `Please select ${what} for the highlighted Sales Rep below.`, error: SALES_REP_ACCOUNT_BLOCK };
}

/** What the server already holds of a campaign's Sales Rep block (the base an edit is diffed on). */
export interface AppliedReps {
  assignments: SalesRepAssignment[];
  emailAccountsByAie: AccountBuckets;
  linkedinAccountIdsByAie: AccountBuckets;
}

/**
 * Removing a sender account from a LAUNCHED campaign, with the same reps: the
 * one change that cannot auto-apply (it would strand the leads already sending
 * from it). The step says so inline instead of silently dropping it.
 */
export const launchedRemovalBlocked = (args: {
  editingNonDraft: boolean;
  launched: boolean;
  applied: AppliedReps;
  draft: Pick<CampaignDraft, 'aiEmployeeAssignments' | 'emailAccountsByAie' | 'linkedinAccountIdsByAie'>;
}): boolean =>
  args.editingNonDraft &&
  args.launched &&
  !hasRepSetChange(args.applied.assignments, args.draft.aiEmployeeAssignments) &&
  senderAccountApplyAction(
    args.applied.emailAccountsByAie,
    args.draft.emailAccountsByAie,
    args.applied.linkedinAccountIdsByAie,
    args.draft.linkedinAccountIdsByAie,
  ) === 'blocked-removal';
